/**
 * InOutCountingResults
 *
 * Design: Flow visualization with directional arrows
 * - Large directional hero cards for In/Out
 * - Net flow visualization with trend indicator
 * - Rate metrics with animated indicators
 */
import React from "react";
import { InOutCountingResults as ResultsType } from "../../types";
import {
  ArrowUpRight,
  ArrowDownRight,
  ArrowLeftRight,
  Activity,
  Clock,
  Video,
  Minus,
  Plus,
  Equal,
} from "lucide-react";

interface InOutCountingResultsProps {
  results: ResultsType;
}

export const InOutCountingResults: React.FC<InOutCountingResultsProps> = ({
  results,
}) => {
  const netFlow = results.net_count;
  const getNetFlowStatus = () => {
    if (netFlow > 0)
      return {
        icon: <Plus className="w-6 h-6" />,
        text: "More entries than exits",
        color: "text-primary-400",
        bg: "from-primary-500/20",
      };
    if (netFlow < 0)
      return {
        icon: <Minus className="w-6 h-6" />,
        text: "More exits than entries",
        color: "text-amber-400",
        bg: "from-amber-500/20",
      };
    return {
      icon: <Equal className="w-6 h-6" />,
      text: "Balanced flow",
      color: "text-emerald-400",
      bg: "from-emerald-500/20",
    };
  };
  const flowStatus = getNetFlowStatus();

  return (
    <div className="space-y-6">
      {/* In/Out Hero Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* IN Card */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-500/20 via-slate-800/50 to-slate-900 border border-primary-500/30 p-8">
          <div className="absolute top-0 right-0 w-48 h-48 bg-primary-500/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-8 -right-8">
            <ArrowUpRight className="w-32 h-32 text-primary-500/10" />
          </div>

          <div className="relative z-10">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg shadow-primary-500/30">
                <ArrowUpRight className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Entries</p>
                <p className="text-primary-400 font-medium">IN</p>
              </div>
            </div>

            <p className="text-6xl font-bold text-white mb-4">
              {results.total_entries}
            </p>

            <div className="flex items-center gap-2 text-slate-400">
              <Activity className="w-4 h-4 text-primary-400" />
              <span className="text-sm">
                {results.entry_rate_per_minute.toFixed(1)} per minute
              </span>
            </div>
          </div>
        </div>

        {/* OUT Card */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500/20 via-slate-800/50 to-slate-900 border border-emerald-500/30 p-8">
          <div className="absolute top-0 right-0 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-8 -right-8">
            <ArrowDownRight className="w-32 h-32 text-emerald-500/10" />
          </div>

          <div className="relative z-10">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                <ArrowDownRight className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="text-slate-400 text-sm">Exits</p>
                <p className="text-emerald-400 font-medium">OUT</p>
              </div>
            </div>

            <p className="text-6xl font-bold text-white mb-4">
              {results.total_exits}
            </p>

            <div className="flex items-center gap-2 text-slate-400">
              <Activity className="w-4 h-4 text-emerald-400" />
              <span className="text-sm">
                {results.exit_rate_per_minute.toFixed(1)} per minute
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Net Flow */}
      <div
        className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${flowStatus.bg} via-slate-800/50 to-slate-900 border border-slate-700/50 p-6`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-slate-800/50 flex items-center justify-center">
              <ArrowLeftRight className="w-6 h-6 text-violet-400" />
            </div>
            <div>
              <p className="text-slate-400 text-sm">Net Flow</p>
              <p className="text-slate-300 text-sm">{flowStatus.text}</p>
            </div>
          </div>

          <div className="text-right">
            <div className="flex items-center gap-2 justify-end">
              <span className={flowStatus.color}>{flowStatus.icon}</span>
              <p className={`text-4xl font-bold ${flowStatus.color}`}>
                {Math.abs(netFlow)}
              </p>
            </div>
            <p className="text-slate-500 text-xs mt-1">
              {netFlow > 0
                ? "more inside"
                : netFlow < 0
                ? "more outside"
                : "equal flow"}
            </p>
          </div>
        </div>
      </div>

      {/* Video Information */}
      <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
            <Video className="w-5 h-5 text-violet-400" />
          </div>
          <h3 className="text-white font-semibold">Video Information</h3>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-slate-400" />
              <span className="text-slate-400 text-xs">Duration</span>
            </div>
            <p className="text-white font-bold text-lg">
              {results.video_duration_seconds}s
            </p>
          </div>

          <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-4 h-4 text-slate-400" />
              <span className="text-slate-400 text-xs">Frame Rate</span>
            </div>
            <p className="text-white font-bold text-lg">
              {results.video_fps} FPS
            </p>
          </div>

          <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50">
            <div className="flex items-center gap-2 mb-2">
              <ArrowLeftRight className="w-4 h-4 text-slate-400" />
              <span className="text-slate-400 text-xs">Line Position</span>
            </div>
            <p className="text-white font-bold text-lg capitalize">
              {results.line_position}
            </p>
          </div>

          <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50">
            <div className="flex items-center gap-2 mb-2">
              <ArrowUpRight className="w-4 h-4 text-slate-400" />
              <span className="text-slate-400 text-xs">Direction</span>
            </div>
            <p className="text-white font-bold text-lg capitalize">
              {results.line_direction}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
